import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import type { PriceImportRowStatus } from './types';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/** Approving 151 rows one at a time is not a product. Capped at 500 by the BE. */
type TData = { data: { updated: number } };
type TError = AxiosError;
type TVariables = {
  runId: number;
  rowIds: number[];
  status: Extract<PriceImportRowStatus, 'approved' | 'skipped' | 'rejected' | 'needs_review'>;
};
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useBulkUpdatePriceImportRowsApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ runId, rowIds, status }: TVariables) => {
    try {
      const { data } = await http.post(`${priceImportsEndpointIdentifier}/${runId}/rows/bulk-status`, {
        rowIds,
        status,
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${priceImportsEndpointIdentifier}/rows/bulk-status`],
    ...options,
  });
}
